import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from "react";
import styles from "./TaskEditorSheet.module.css";
import CreatorFragment, {
  CreatorFragmentCommonProps,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import Section from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/Section/Section";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import Header from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/Header/Header";
import {
  PatchTask,
  Task,
} from "@coworker/apprestructured/src/task-management/types/Task";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { NotFound } from "@coworker/apprestructured/src/task-management/components/NotFound/NotFound";
import { useTranslation } from "@coworker/locales";
import { useUpdateTask } from "@coworker/apprestructured/src/task-management/services/useUpdateTask";
import { merge } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useTask } from "@coworker/apprestructured/src/task-management/services/useTask";
import useSaveGuard from "@coworker/apprestructured/src/save-guard/useSaveGuard";
import CreateTaskSchedule, {
  CreateTaskScheduleChangeEvent,
} from "@coworker/apprestructured/src/task-management/components/ScheduleTask/CreateTaskSchedule/CreateTaskSchedule";
import { AddImages } from "@coworker/apprestructured/src/task-management/components/AddImages/AddImages";
import AttachFiles from "@coworker/apprestructured/src/task-management/components/AttachFiles/AttachFiles";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { useTaskImages } from "@coworker/apprestructured/src/task-management/services/useTaskImages";
import { useTaskFiles } from "@coworker/apprestructured/src/task-management/services/useTaskFiles";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { invalidateTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import { useQueryClient } from "@tanstack/react-query";
import {
  isNewUpload,
  NewUpload,
} from "@coworker/apprestructured/src/task-management/types/Upload";
import { useFileUploader } from "@coworker/apprestructured/src/uploader/useFileUploader";
import SomethingWentWrongPrompt from "../../SomethingWentWrongPrompt/SomethingWentWrongPrompt";

const NO_IMAGES: NewOrExistingFile[] = [];
const NO_FILES: NewOrExistingFile[] = [];

/**
 * The TaskEditorSheet is open for the task with id 'taskEditor' taken from the url
 * search params. Open/close the sheet by adding/removing a 'taskEditor'
 * search param, or use the 'useSearchParamState' hook to reduce some boilerplate.
 */
function TaskEditorSheet() {
  const [{ taskEditor: taskId }, setSearchParams] = useSearchParamState();
  // Includes most unsaved changes the user has made to the task.
  const [taskChanges, setTaskChanges] = useState<Record<string, unknown>>({});
  // Images and files are handled separately because the data set when the user
  // makes changes is not the same as we send to the backend, so it cannot
  // be easily merged with the original task.
  // Is only undefined when the user has made no changes.
  const [patchTaskImages, setPatchTaskImages] = useState<
    NewOrExistingFile[] | undefined
  >(undefined);
  const [patchTaskFiles, setPatchTaskFiles] = useState<
    NewOrExistingFile[] | undefined
  >(undefined);
  const [hasClickedSave, setHasClickedSave] = useState(false);
  const [showSomethingWentWrongPrompt, setShowSomethingWentWrongPrompt] =
    useState(false);

  const { t } = useTranslation();

  const { mutateAsync: updateTask } = useUpdateTask();
  const { data: allTaskTypes, isLoading: isLoadingTaskTypes } =
    useAllTaskTypes();
  const { data: originalTask, isFetching: isFetchingOriginalTask } =
    useTask(taskId);
  const { data: originalImages } = useTaskImages(
    taskId,
    originalTask?.image_ids
  );
  const { data: originalFiles } = useTaskFiles(taskId, originalTask?.file_ids);

  const {
    ready: imageUploaderIsReady,
    upload: uploadImages,
    newOrExistingUploads: newOrExistingImageUploads,
  } = useImageUploader(patchTaskImages ?? NO_IMAGES);

  const {
    ready: fileUploaderIsReady,
    upload: uploadFiles,
    newOrExistingUploads: newOrExistingFileUploads,
  } = useFileUploader(patchTaskFiles ?? NO_FILES);

  // This is the body sent to the backend when saving and includes all changes.
  const patchTask = useMemo<PatchTask>(() => {
    const patchTask: PatchTask = { ...taskChanges };
    if (patchTaskImages !== undefined) {
      patchTask.images = newOrExistingImageUploads;
    }
    if (patchTaskFiles !== undefined) {
      patchTask.files = newOrExistingFileUploads;
    }
    return patchTask;
  }, [
    newOrExistingFileUploads,
    newOrExistingImageUploads,
    patchTaskFiles,
    patchTaskImages,
    taskChanges,
  ]);

  // Clear all changes when the task id changes in the url.
  const handleTaskIdChange = useEffectEvent(() => {
    setTaskChanges({});
    setPatchTaskImages(undefined);
    setPatchTaskFiles(undefined);
    setHasClickedSave(false);
  });

  useEffect(() => {
    handleTaskIdChange();
  }, [taskId]);

  const taskType = useMemo(() => {
    if (!allTaskTypes || !originalTask) {
      return undefined;
    }
    return allTaskTypes.find(({ id }) => id === originalTask.task_type_id);
  }, [originalTask, allTaskTypes]);

  const { enableSaveGuard, disableSaveGuard } = useSaveGuard();

  const queryClient = useQueryClient();

  const handleMutationResults = useCallback(
    (
      updateTaskResult: PromiseSettledResult<Task>,
      imageUploadResult: PromiseSettledResult<void>,
      fileUploadResult: PromiseSettledResult<void>
    ) => {
      handleImageUploadResult(
        imageUploadResult,
        newOrExistingImageUploads.filter(isNewUpload)
      );
      handleFileUploadResult(
        fileUploadResult,
        newOrExistingFileUploads.filter(isNewUpload)
      );
      if (updateTaskResult.status === "fulfilled") {
        invalidateTaskQueries(queryClient);
        disableSaveGuard();
        setSearchParams((prev) => {
          prev.delete("taskEditor");
          prev.set("taskViewer", String(updateTaskResult.value["id"]));
        });
      } else {
        setShowSomethingWentWrongPrompt(true);
      }
    },
    [
      disableSaveGuard,
      newOrExistingFileUploads,
      newOrExistingImageUploads,
      queryClient,
      setSearchParams,
    ]
  );

  const handleSaveClick = useCallback(() => {
    setHasClickedSave(true);
    async function fn() {
      if (taskId) {
        // We can skip saving if no changes are made
        if (Object.keys(patchTask).length > 0) {
          const [updateTaskResult, imageUploadResult, fileUploadResult] =
            await Promise.allSettled([
              updateTask({
                id: taskId,
                patchTask,
              }),
              uploadImages(),
              uploadFiles(),
            ]);
          handleMutationResults(
            updateTaskResult,
            imageUploadResult,
            fileUploadResult
          );
        } else {
          setSearchParams((prev) => {
            prev.delete("taskEditor");
            prev.set("taskViewer", taskId);
          });
        }
      }
    }
    fn();
  }, [
    taskId,
    patchTask,
    updateTask,
    uploadImages,
    uploadFiles,
    handleMutationResults,
    setSearchParams,
  ]);

  const handleCloseClick = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("taskEditor");
    });
  }, [setSearchParams]);

  const loadingFailed =
    !isFetchingOriginalTask &&
    !isLoadingTaskTypes &&
    (!originalTask || !taskType);

  const header = useMemo(() => {
    if (loadingFailed) {
      return <FixaModalHeader closeBtnClick={handleCloseClick} />;
    } else {
      return (
        <FixaModalHeader
          closeBtnClick={handleCloseClick}
          title={t("editTaskString")}
        />
      );
    }
  }, [handleCloseClick, loadingFailed, t]);

  const footer = useMemo(() => {
    if (loadingFailed) {
      return null;
    } else {
      return (
        <FixaModalFooter>
          <FixaButton
            onClick={handleSaveClick}
            text={t("saveTaskString")}
            fluid
            type="primary"
            loading={hasClickedSave}
            disabled={
              !taskType ||
              !originalTask ||
              !taskId ||
              !imageUploaderIsReady ||
              !fileUploaderIsReady
            }
            htmlType="button"
          />
        </FixaModalFooter>
      );
    }
  }, [
    loadingFailed,
    handleSaveClick,
    t,
    hasClickedSave,
    taskType,
    originalTask,
    taskId,
    imageUploaderIsReady,
    fileUploaderIsReady,
  ]);

  const handleTaskChange = useCallback(
    (part: Record<string, unknown>) => {
      setTaskChanges((prev) => merge(prev, part));
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const blockInput = useMemo(() => {
    return hasClickedSave || isFetchingOriginalTask || !taskId;
  }, [hasClickedSave, isFetchingOriginalTask, taskId]);

  const mergedTask = useMemo(() => {
    return originalTask ? merge(originalTask, patchTask) : LOADING;
  }, [originalTask, patchTask]);

  const creatorFragmentCommonProps: CreatorFragmentCommonProps = useMemo(() => {
    return {
      blockInput: blockInput,
      task: mergedTask,
      onTaskChange: handleTaskChange,
      showValidation: false,
    };
  }, [blockInput, mergedTask, handleTaskChange]);

  const handleScheduleChange = useCallback(
    ({ futureStart }: CreateTaskScheduleChangeEvent) => {
      handleTaskChange({ start: futureStart });
      // Ignore other values for now
    },
    [handleTaskChange]
  );

  const futureStart = useMemo(() => {
    if (isLoading(mergedTask) || mergedTask["start"] === null) {
      return "";
    } else {
      return mergedTask["start"] as string;
    }
  }, [mergedTask]);

  const handleImagesChange = useCallback(
    (newImages: NewOrExistingFile[]) => {
      enableSaveGuard();
      setPatchTaskImages(newImages);
    },
    [enableSaveGuard]
  );

  const handleFilesChange = useCallback(
    (newFiles: NewOrExistingFile[]) => {
      enableSaveGuard();
      setPatchTaskFiles(newFiles);
    },
    [enableSaveGuard]
  );

  const images = patchTaskImages ?? originalImages ?? NO_IMAGES;
  const files = patchTaskFiles ?? originalFiles ?? NO_FILES;

  const handleRefreshClick = useCallback(() => {
    disableSaveGuard();
    setTimeout(() => {
      window.location.reload();
    }, 0);
  }, [disableSaveGuard]);

  const content = useMemo(() => {
    if (loadingFailed) {
      return (
        <NotFound
          title={t("sorryTaskCouldNotBeFoundString")}
          message={t("thisTaskDoesNotExistString")}
        />
      );
    } else {
      return (
        <div className={styles["sheetsContent"]}>
          <Section>
            <Header number="1" label={t("taskDetailsString")} />
            <FixaListView id="details">
              {taskType ? (
                taskType.creator_fragments.map((config, i) => {
                  return (
                    <CreatorFragment
                      key={i}
                      config={config}
                      commonProps={creatorFragmentCommonProps}
                    />
                  );
                })
              ) : (
                <LoadingCreatorFragment />
              )}
            </FixaListView>
          </Section>
          <Section>
            <Header number="2" label={t("taskSettingsString")} />
            <FixaListView id="settings">
              <CreatorFragment
                config={{ id: "assign-to-creator-fragment", props: {} }}
                commonProps={creatorFragmentCommonProps}
              />
              <CreatorFragment
                config={{
                  id: "switch-creator-fragment",
                  props: {
                    field: "priority",
                    label: t("priorityString"),
                    icon: "notice-circle",
                  },
                }}
                commonProps={creatorFragmentCommonProps}
              />
              <CreateTaskSchedule
                disabled={blockInput}
                futureStart={futureStart}
                onChange={handleScheduleChange}
                // Ignore these for now
                repeatingStart=""
                end=""
                recurrence={null}
              />
              <AddImages
                images={images}
                onConfirmClick={handleImagesChange}
                disabled={blockInput}
              />
              <AttachFiles
                disabled={blockInput}
                files={files}
                onFilesChanged={handleFilesChange}
              />
            </FixaListView>
          </Section>
          {showSomethingWentWrongPrompt && (
            <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
          )}
        </div>
      );
    }
  }, [
    loadingFailed,
    t,
    taskType,
    creatorFragmentCommonProps,
    blockInput,
    futureStart,
    handleScheduleChange,
    images,
    handleImagesChange,
    files,
    handleFilesChange,
    showSomethingWentWrongPrompt,
    handleRefreshClick,
  ]);

  return (
    <FixaModal visible={!!taskId} handleCloseBtn={handleCloseClick}>
      <FixaSheets
        fullSize
        className={loadingFailed ? "" : styles["greyBackground"]}
        header={header}
        footer={footer}
      >
        {content}
      </FixaSheets>
    </FixaModal>
  );
}

function handleImageUploadResult(
  imageUploadResult: PromiseSettledResult<void>,
  newImageUploads: NewUpload[]
) {
  if (imageUploadResult.status === "rejected") {
    const bucketIds = newImageUploads.map(
      ({ createUpload }) => createUpload.bucket_id
    );
    reportMessageToSentry("Image upload failed", { bucketIds }, "error");
  }
}

function handleFileUploadResult(
  fileUploadResult: PromiseSettledResult<void>,
  newFileUploads: NewUpload[]
) {
  if (fileUploadResult.status === "rejected") {
    const bucketIds = newFileUploads.map(
      ({ createUpload }) => createUpload.bucket_id
    );
    reportMessageToSentry("File upload failed", { bucketIds }, "error");
  }
}

export default TaskEditorSheet;
